import { Link, Navigate } from 'react-router'
import { Briefcase, HandCoins, ListChecks, LogOut, Rocket, Users } from 'lucide-react'
import { useAuth } from '@/auth/useAuth'
import { useMe } from '@/auth/useMe'
import { PageNote } from '@/components/PageNote'
import { Button } from '@/components/ui/button'
import {
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import logoRgb from '@/assets/brand/Logo_RGB.png'

export function LandingPage() {
  const { signOut } = useAuth()
  const { me, loading } = useMe()
  const firstName = me?.full_name.trim().split(/\s+/)[0] ?? ''

  // Founders have no pre-/post-invest choice — send them straight to the portal
  // (no dead end). Wait for the profile so we don't flash the wrong buttons.
  if (loading) {
    return (
      <main className="flex min-h-svh items-center justify-center">
        <PageNote>Bezig met laden…</PageNote>
      </main>
    )
  }
  if (me?.role === 'founder') {
    return <Navigate to="/founder" replace />
  }
  // The fundraiser (spec-54) keeps the landing choice — pre-/post-invest and
  // the fund-leads boards are all daily destinations — but admin/team-only
  // tiles (taken-hub, archived fundraising module) are hidden below.
  const isFundraiser = me?.role === 'fundraiser'
  // Fail closed: without a loaded profile (fetch error or missing core.users
  // row) we cannot rule out a founder, so never show the admin/team buttons.
  if (me === null) {
    return (
      <main className="flex min-h-svh items-center justify-center p-6">
        <div className="vonk-card flex max-w-md flex-col items-center gap-4 text-center">
          <p className="text-foreground">Profiel laden mislukt. Probeer het opnieuw.</p>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => window.location.reload()}>
              Opnieuw proberen
            </Button>
            <Button variant="ghost" onClick={() => void signOut()}>
              <LogOut className="size-4" />
              Uitloggen
            </Button>
          </div>
        </div>
      </main>
    )
  }

  // Landing-page section buttons share a chunkier mobile treatment so the
  // stacked list on a phone does not read as thin strips: at least 56px tall,
  // slightly bigger label and larger icons. They are pills like every Button
  // (spec-64), so there is no mobile radius override. On md+ the buttons keep
  // their existing desktop look (48px, 20px icons) — the extra `max-md:`
  // utilities only apply below 768px, the single spec-53 breakpoint. `min-h`
  // is used instead of `h` so we don't fight the base Button's own
  // `max-md:h-11` tap-target rule. No font weight here: the variant sets it
  // (700 for the primary rows, 600 for "Mijn taken"), and a weight in this
  // string would replace the primary's 700 (spec-64, U-219).
  const sectionButtonBase =
    'h-12 text-base transition hover:-translate-y-0.5' +
    ' max-md:min-h-14 max-md:px-5 max-md:text-[1.0625rem]' +
    ' max-md:[&_svg]:size-6'
  // The four top-row buttons share the width evenly on desktop; the solo
  // "Mijn taken" row below does not grow.
  const sectionButtonRowClass = `${sectionButtonBase} flex-1`

  return (
    <main className="flex min-h-svh items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <div className="vonk-card flex flex-col gap-4">
          <CardHeader className="text-center">
            <img src={logoRgb} alt="VONK Ventures" className="mx-auto mb-4 w-4/5" />
            <CardTitle className="text-xl">Welkom, {firstName}</CardTitle>
            <CardDescription>Waar wil je aan de slag?</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 max-md:gap-3.5">
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className={sectionButtonRowClass}>
                <Link to="/preinvest">
                  <Rocket className="size-5" />
                  Pre-invest
                </Link>
              </Button>
              {/* Fundraising is archived (spec-30): the card only renders while
                  the backend reports FUNDRAISING_UI_ENABLED via /me. */}
              {me?.fundraising_enabled && !isFundraiser ? (
                <Button asChild size="lg" className={sectionButtonRowClass}>
                  <Link to="/fundraising">
                    <HandCoins className="size-5" />
                    Fundraising
                  </Link>
                </Button>
              ) : null}
              <Button asChild size="lg" className={sectionButtonRowClass}>
                <Link to="/postinvest">
                  <Briefcase className="size-5" />
                  Post-invest
                </Link>
              </Button>
              {/* Relaties (spec-44): CRM-module. Zichtbaar voor admin/team —
                  founders bereiken deze pagina nooit (redirect hierboven). */}
              <Button asChild size="lg" className={sectionButtonRowClass}>
                <Link to="/relaties">
                  <Users className="size-5" />
                  Relaties
                </Link>
              </Button>
            </div>
            {/* Global "Mijn taken" (spec-19b E): a top-level destination for the
                owner's tasks across pre-invest + wasstraat, with an admin-only
                all-users operator view behind it. Admin/team only — the /taken
                route bounces a fundraiser anyway. */}
            {!isFundraiser ? (
              <Button
                asChild
                size="lg"
                variant="secondary"
                className={sectionButtonBase}
              >
                <Link to="/taken">
                  <ListChecks className="size-5" />
                  Mijn taken
                </Link>
              </Button>
            ) : null}
            <Button
              variant="ghost"
              className="self-end text-muted-foreground"
              onClick={() => void signOut()}
            >
              <LogOut className="size-4" />
              Uitloggen
            </Button>
          </CardContent>
        </div>
      </div>
    </main>
  )
}
